import React, { Fragment, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { PageHeader, SearchEmpty } from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { AdminDashboardSkeleton } from "features/AdminDashboard/AdminDashboardSkeleton";
import { GenericMetric } from "features/AdminDashboard/GenericMetric";

import { ChartItem, dataAnalysisServices } from "services/DataAnalysis";

import BoxFolder from "assets/svg/BoxFolder.svg";
import ErrorCircle from "assets/svg/ErrorCircle.svg";
import Player from "assets/svg/Player.svg";

import { DASHBOARD, FORMS_HEARING } from "utils/entities/permission";

import { capitalizeFirstLetter, getTranslatedDescription } from "./constants";
import { IDashboard } from "./interface";
import { Container, Content } from "./styles";

export const AdminDashboard: React.FC<IDashboard> = ({ isFull }) => {
  const { t } = useTranslation();
  const { userCustomerProfile } = useSelector((store: any) => store.profile);
  const [dashboardData, setDashboardData] = useState<ChartItem[]>([]);
  const { user } = useAppSelector((state) => state.user);
  const { viewOnlyYourRecords } = usePermissions(DASHBOARD.ADMIN);
  const [information, setInformation] = useState<{
    title: string;
    data: ChartItem[];
  } | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const { addToast } = useToast();

  function getAllC1(): Array<any> {
    return dashboardData.map((item, i) => {
      let newItem = null;
      const desc = capitalizeFirstLetter(item.description);
      const translatedDesc = getTranslatedDescription(desc);

      if (desc === "Com erro") {
        newItem = {
          labelButton: t("dashboard.view"),
          color: "#EB58613D",
          svg: ErrorCircle,
          title: translatedDesc,
          value: item.total,
        };
        return newItem;
      }

      const randomString = i < 4 ? BoxFolder : Player;
      newItem = {
        labelButton: t("dashboard.publishNow"),
        color: "rgba(240, 202, 104, 0.24)",
        svg: randomString,
        title: translatedDesc,
        value: item.total,
      };
      return newItem;
    });
  }

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setInformation(null);

      if (userCustomerProfile?.placeId) {
        setLoading(true);
        try {
          const response = await dataAnalysisServices.GetDashboardInformation(
            userCustomerProfile?.placeId,
            3,
            viewOnlyYourRecords ? user?.id : null,
          );

          if (isMounted) {
            const data = Array.isArray(response?.data) ? response.data : [];
            setDashboardData(data);
          }
        } catch {
          addToast({
            type: "error",
            title: t("dashboard.fetchError"),
            description: t("dashboard.fetchErrorDescription"),
          });

          setDashboardData([]);
        } finally {
          if (isMounted) setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [userCustomerProfile?.placeId]);

  const { read } = usePermissions(FORMS_HEARING.HEARING_FILE);

  if (loading) {
    return <AdminDashboardSkeleton isFull={isFull} />;
  }

  return (
    <Fragment>
      <Container isFull={isFull}>
        <PageHeader
          quantity={0}
          id="header"
          label={t("dashboard.title")}
          background="secondary"
          addClick={undefined}
          disabled={false}
        />
        {read ? (
          <Content>
            {getAllC1().length > 0 && !loading ? (
              getAllC1().map((item) => (
                <GenericMetric
                  key={item.title}
                  color={item.color}
                  svg={item.svg}
                  title={item.title}
                  value={item.value}
                />
              ))
            ) : information ? (
              <SearchEmpty
                textMessage={t("dashboard.noDataToDisplay")}
                typeIcon="file"
                marginTop="40px"
              />
            ) : null}
          </Content>
        ) : (
          <SearchEmpty
            textMessage={t("common.noPermissionForProfile")}
            typeIcon="avatar"
            marginTop="40px"
          />
        )}
      </Container>
    </Fragment>
  );
};
